import {
  IQuoteRequestSchema,
  QuoteRequestSchema,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { Controller, useFormContext } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../api/axios';
import { MBox, QuoteSelectInput } from '../../../../components/Monetize';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { QuoteBasicAccount } from '../../../../components/Quotes/QuoteBasicAccount';
import { QuoteBasicInfoLine } from '../../../../components/Quotes/QuoteBasicInfoLine';
import { QuoteContractLengthPeriodPopover } from '../../../../components/Quotes/QuoteContractLengthPeriodPopover';
import { QuoteContractRenewalTermsActionV2 } from '../../../../components/Quotes/QuoteContractRenewalTermsActionV2';
import { QuoteRenewalUpliftRow } from '../../../../components/Quotes/RenewalUplift/QuoteRenewalUpliftRow';
import { useExperienceConfiguration, useNetTerms } from '../../../../hooks';
import { useFlags } from '../../../../services/launchDarkly';
import { getQuoteType } from '../../../../utils';
import {
  CONTRACT_TERM_FIELD_KEYS,
  RENEWAL_ACTION_FIELD_KEYS,
} from '../../../../utils/quoteFieldValidations';
import { useQuoteContext } from '../../Quote/quoteContext';

interface QuoteBasicContractProps {
  onModifiedAccount?: () => void;
}

export const QuoteBasicContract = ({
  onModifiedAccount,
}: QuoteBasicContractProps) => {
  const {
    handleSubmitButtonWithoutDirtyCheck,
    isReadOnly,
    isReadOnlyQuote,
    quoteData: { quote, updateQuote, setQuote, fetchQuote, evaluateQuoteRules },
    quoteSettingsData,
  } = useQuoteContext();
  const { isNew, isAmendment, isRenewal } = getQuoteType(quote);
  const { control, getValues, setValue } =
    useFormContext<IQuoteRequestSchema>();
  const { hideAutoRenewal } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const { activeNetTerms } = useNetTerms(getValues('netTerms'));
  const { enableQuoteLevelUplift } = useFlags();

  // handle saving quote contract renewal terms and custom billing day
  const handleQuoteBasicInfoUpdate = async (
    data: IQuoteRequestSchema,
  ): Promise<void> => {
    try {
      const payload = QuoteRequestSchema.parse(data);
      await updateQuote(quote?.id!, payload);
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  if (!quote) {
    return null;
  }

  return (
    <MBox>
      <QuoteBasicInfoLine title="Account" fieldKey="accountId">
        <QuoteBasicAccount
          quote={quote}
          isReadOnlyQuote={isReadOnlyQuote}
          onSaved={(account) => {
            setQuote({ ...quote, accountName: account.accountName });
            if (quote.status === QuoteStatusEnum.DRAFT) {
              evaluateQuoteRules(quote.id);
            } else {
              fetchQuote(quote.id);
            }
            onModifiedAccount?.();
          }}
        />
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine
        title="Contract Term"
        fieldKey={CONTRACT_TERM_FIELD_KEYS}
      >
        <QuoteContractLengthPeriodPopover
          isCustomContractLength={
            quoteSettingsData.quoteSettings?.customContractLength
              .allowCustomLength!
          }
          isNew={isNew}
          isAmendment={isAmendment}
          quote={quote}
          setValue={setValue}
          handleSubmitButton={handleSubmitButtonWithoutDirtyCheck}
          isReadOnly={isReadOnly}
          isRenewal={isRenewal}
          setQuote={setQuote}
        />
      </QuoteBasicInfoLine>

      {!hideAutoRenewal && (
        <QuoteBasicInfoLine
          title="Renewal Action"
          fieldKey={RENEWAL_ACTION_FIELD_KEYS}
        >
          <QuoteContractRenewalTermsActionV2
            contractRenewalTerms={quote?.contractRenewalTerms}
            defaultContractRenewalTerms={
              quoteSettingsData.quoteSettings?.contractRenewalTerms
            }
            onSubmit={async (contractRenewalTerms) => {
              try {
                await handleQuoteBasicInfoUpdate({
                  ...getValues(),
                  contractRenewalTerms,
                });
                setValue('contractRenewalTerms', contractRenewalTerms, {
                  shouldDirty: false,
                });
              } catch (ex) {
                handleApiErrorToast(ex);
              }
            }}
            isReadOnly={isReadOnly}
          />
        </QuoteBasicInfoLine>
      )}

      {/* Renewal uplift is configured independently of the auto-renewal display */}
      {enableQuoteLevelUplift && <QuoteRenewalUpliftRow />}

      <QuoteBasicInfoLine title="Net Terms" fieldKey="netTerms">
        <MBox mr={isReadOnly ? 0 : '-3'}>
          <Controller
            name="netTerms"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <MCustomSelect
                isDisabled={isReadOnly}
                isReadOnly={isReadOnly}
                MainInputComponent={QuoteSelectInput}
                itemTitle="name"
                itemValue="value"
                items={activeNetTerms}
                popOverProps={{
                  positioning: { placement: 'bottom-end', sameWidth: false },
                }}
                isLazy
                popOverContentProps={{ minWidth: '10rem' }}
                onChange={(e: any) => {
                  onChange(e);
                  handleSubmitButtonWithoutDirtyCheck();
                }}
                showContentInInput
                {...rest}
              />
            )}
          />
        </MBox>
      </QuoteBasicInfoLine>
    </MBox>
  );
};
